import { useCallback, useEffect, useState } from "react";
import axios from "axios";
import { useNavigate } from "react-router-dom";
import { Building2, Search } from "lucide-react";
import { useAuth, formatApiErrorDetail } from "@/context/AuthContext";
import { EmptyState, Panel, StatusBadge, formatDate } from "@/components/admin/ui";
import { usePolling } from "@/hooks/usePolling";
import { usePageLastUpdated } from "@/context/PageMetaContext";

const ORG_STATUSES = ["Active", "Onboarding", "Paused", "Suspended", "Inactive"];

export default function AdminOrganizations() {
  const { API } = useAuth();
  const navigate = useNavigate();
  const [organizations, setOrganizations] = useState([]);
  const [industries, setIndustries] = useState([]);
  const [search, setSearch] = useState("");
  const [status, setStatus] = useState("");
  const [industry, setIndustry] = useState("");
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState("");
  const [lastUpdated, setLastUpdated] = useState(null);

  const load = useCallback(async (silent) => {
    if (!silent) setLoading(true);
    if (!silent) setError("");
    try {
      const params = {};
      if (search.trim()) params.search = search.trim();
      if (status) params.status = status;
      if (industry) params.industry = industry;
      const { data } = await axios.get(`${API}/admin/organizations`, { params, withCredentials: true });
      setOrganizations(data.organizations || []);
      setIndustries(data.industries || []);
      setLastUpdated(Date.now());
    } catch (err) {
      if (!silent) setError(formatApiErrorDetail(err.response?.data?.detail));
    } finally {
      if (!silent) setLoading(false);
    }
  }, [API, search, status, industry]);

  useEffect(() => {
    const timer = window.setTimeout(load, search ? 250 : 0);
    return () => window.clearTimeout(timer);
  }, [load, search]);

  usePolling(() => load(true), 10000);

  usePageLastUpdated(lastUpdated, "organizations-last-updated");

  return (
    <div className="space-y-6" data-testid="admin-organizations-page">
      <Panel testId="organizations-table-panel">
        <div className="flex flex-col gap-3 lg:flex-row lg:items-center lg:justify-between">
          <label className="relative block">
            <Search size={16} className="pointer-events-none absolute left-3 top-1/2 -translate-y-1/2 text-slate-400 dark:text-slate-500" />
            <input
              value={search}
              onChange={(e) => setSearch(e.target.value)}
              data-testid="organization-search-input"
              placeholder="Search organizations…"
              className="w-full rounded-xl border border-slate-300 bg-white py-2.5 pl-9 pr-4 text-sm focus:border-blue-600 focus:outline-none focus:ring-2 focus:ring-blue-600/10 lg:w-80 dark:border-white/15 dark:bg-white/5 dark:text-white dark:placeholder:text-slate-500"
            />
          </label>
          <div className="flex flex-col gap-3 sm:flex-row">
            <select value={status} onChange={(e) => setStatus(e.target.value)} data-testid="organization-status-filter" className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
              <option value="">All statuses</option>
              {ORG_STATUSES.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
            <select value={industry} onChange={(e) => setIndustry(e.target.value)} data-testid="organization-industry-filter" className="rounded-xl border border-slate-300 bg-white px-4 py-2.5 text-sm font-semibold dark:border-white/15 dark:bg-white/5 dark:text-white">
              <option value="">All industries</option>
              {industries.map((item) => <option key={item} value={item}>{item}</option>)}
            </select>
          </div>
        </div>

        {error && <p className="mt-5 rounded-xl bg-red-50 px-4 py-3 text-sm font-semibold text-red-700 dark:bg-red-500/10 dark:text-red-400" data-testid="organizations-error">{error}</p>}
        <div className="mt-6">
          {loading ? (
            <div className="rounded-xl border border-slate-200 bg-slate-50 p-10 text-center text-sm font-semibold text-slate-500 dark:border-white/10 dark:bg-white/[0.03] dark:text-slate-400" data-testid="organizations-loading">Loading organizations…</div>
          ) : organizations.length === 0 ? (
            <EmptyState icon={Building2} title="No organizations yet." message="Organizations appear here after the separate onboarding process." testId="organizations-empty" />
          ) : (
            <div className="overflow-x-auto">
              <table className="w-full min-w-[920px] text-left text-sm" data-testid="organizations-table">
                <thead>
                  <tr className="border-b border-slate-200 text-xs uppercase tracking-[0.12em] text-slate-400 dark:border-white/10 dark:text-slate-500">
                    <th className="py-3 pr-4 font-bold">Organization</th>
                    <th className="py-3 pr-4 font-bold">Industry</th>
                    <th className="py-3 pr-4 font-bold">Status</th>
                    <th className="py-3 pr-4 font-bold">Users</th>
                    <th className="py-3 pr-4 font-bold">Active Solutions</th>
                    <th className="py-3 pr-4 font-bold">Created</th>
                    <th className="py-3 font-bold">Action</th>
                  </tr>
                </thead>
                <tbody>
                  {organizations.map((item) => (
                    <tr key={item.org_id} className="border-b border-slate-100 last:border-0 hover:bg-slate-50/60 dark:border-white/5 dark:hover:bg-white/5" data-testid={`organization-row-${item.org_id}`}>
                      <td className="py-4 pr-4 font-bold text-slate-950 dark:text-white">{item.name}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{item.industry || "—"}</td>
                      <td className="py-4 pr-4"><StatusBadge status={item.status} testId={`organization-status-${item.org_id}`} /></td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{item.users_count}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{item.active_solutions_count}</td>
                      <td className="py-4 pr-4 text-slate-600 dark:text-slate-400">{formatDate(item.created_at)}</td>
                      <td className="py-4">
                        <button
                          type="button"
                          onClick={() => navigate(`/admin/organizations/${item.org_id}`)}
                          data-testid={`organization-view-${item.org_id}`}
                          className="rounded-lg border border-slate-200 px-3 py-1.5 text-xs font-bold text-slate-700 hover:bg-white dark:border-white/15 dark:text-slate-200 dark:hover:bg-white/10"
                        >
                          View
                        </button>
                      </td>
                    </tr>
                  ))}
                </tbody>
              </table>
            </div>
          )}
        </div>
      </Panel>
    </div>
  );
}
